/**
 * Email templates for different order statuses
 * These templates can be used to notify customers when their order status changes
 */

import { formatCurrency } from '@/utils/currencyUtils';

// Get order number for display in emails
const getOrderNumber = (order: any): string => {
  // Use display_id if available, otherwise fallback to shortened UUID
  if (order.display_id) {
    return `#${order.display_id}`;
  }
  return order.id.substring(0, 8);
};

// Generate order items list for email
const generateOrderItemsList = (items: any[]): string => {
  return items.map(item => 
    `<tr>
      <td style="padding: 8px; border-bottom: 1px solid #eee;">${item.product.name}</td>
      <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${item.quantity} st</td>
      <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${formatCurrency(item.quantity * item.product.price)}</td>
    </tr>`
  ).join('');
};

// Generate order status email content
export const getOrderStatusEmailTemplate = (order: any, status: string): string => {
  const orderNumber = getOrderNumber(order);
  
  // Format the order date
  const orderDate = new Date(order.created_at).toLocaleDateString('sv-SE');
  
  // Get status display text
  const statusText = {
    'pending': 'Väntar på bekräftelse',
    'confirmed': 'Bekräftad',
    'processing': 'Under behandling',
    'shipped': 'Skickad',
    'delivered': 'Levererad',
    'completed': 'Slutförd',
    'cancelled': 'Avbruten'
  }[status] || status;
  
  // Generate items list
  const itemsList = order.order_items ? generateOrderItemsList(order.order_items) : '';
  
  // Calculate total
  const total = order.total_amount || 
    (order.order_items ? order.order_items.reduce((sum: number, item: any) => 
      sum + (item.quantity * (item.product ? item.product.price : 0)), 0) : 0);
  
  return `
    <div style="font-family: sans-serif; max-width: 600px; margin: 0 auto;">
      <div style="background-color: #f8f9fa; padding: 20px; text-align: center; border-bottom: 3px solid #28a745;">
        <h1 style="color: #28a745; margin: 0;">Orderuppdatering</h1>
        <p>Order ${orderNumber} är nu: <strong>${statusText}</strong></p>
      </div>
      
      <div style="padding: 20px;">
        <h2>Hej ${order.profiles?.first_name || 'kund'}!</h2>
        <p>Tack för din beställning hos Lokala Producenter. Här kommer en uppdatering om din order.</p>
        
        <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
          <tr>
            <td style="padding: 10px; background-color: #f8f9fa; border-bottom: 2px solid #28a745;"><strong>Ordernummer:</strong></td>
            <td style="padding: 10px; background-color: #f8f9fa; border-bottom: 2px solid #28a745;">${orderNumber}</td>
          </tr>
          <tr>
            <td style="padding: 10px; border-bottom: 1px solid #eee;"><strong>Datum:</strong></td>
            <td style="padding: 10px; border-bottom: 1px solid #eee;">${orderDate}</td>
          </tr>
          <tr>
            <td style="padding: 10px; border-bottom: 1px solid #eee;"><strong>Status:</strong></td>
            <td style="padding: 10px; border-bottom: 1px solid #eee;">${statusText}</td>
          </tr>
        </table>
        
        <h3>Din beställning</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <thead>
            <tr style="background-color: #f8f9fa;">
              <th style="padding: 8px; text-align: left; border-bottom: 2px solid #28a745;">Produkt</th>
              <th style="padding: 8px; text-align: right; border-bottom: 2px solid #28a745;">Antal</th>
              <th style="padding: 8px; text-align: right; border-bottom: 2px solid #28a745;">Pris</th>
            </tr>
          </thead>
          <tbody>
            ${itemsList}
          </tbody>
          <tfoot>
            <tr>
              <td colspan="2" style="padding: 8px; text-align: right; border-top: 2px solid #28a745;"><strong>Totalt:</strong></td>
              <td style="padding: 8px; text-align: right; border-top: 2px solid #28a745;"><strong>${formatCurrency(total)}</strong></td>
            </tr>
          </tfoot>
        </table>
        
        <div style="margin-top: 30px; padding: 15px; background-color: #f8f9fa; border-left: 3px solid #28a745;">
          <h3 style="margin-top: 0;">Leveransuppgifter</h3>
          <p><strong>Adress:</strong> ${order.address || 'Ingen adress angiven'}</p>
          <p><strong>Leveransdatum:</strong> ${order.delivery_date || 'Ej angivet'}</p>
          <p><strong>Leveranstid:</strong> ${order.delivery_time || 'Ej angivet'}</p>
          ${order.delivery_notes ? `<p><strong>Instruktioner:</strong> ${order.delivery_notes}</p>` : ''}
        </div>
        
        <p style="margin-top: 30px;">Har du frågor om din beställning? Kontakta oss på <a href="mailto:info@lokalaproducenter.se">info@lokalaproducenter.se</a>.</p>
        
        <p>Med vänliga hälsningar,<br>Lokala Producenter</p>
      </div>
      
      <div style="background-color: #f8f9fa; padding: 15px; text-align: center; font-size: 12px; color: #6c757d;">
        <p>© ${new Date().getFullYear()} Lokala Producenter. Alla rättigheter förbehållna.</p>
      </div>
    </div>
  `;
}; 